import { useEffect, useState } from "react";
import { Availability, CheckInValidationType } from "types";
import { CheckInPreferenceFormValues } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import {
    getDurationMinutes,
    getMutatedValidations,
    getValidationByType,
    useCheckInValidationField,
} from "@/components/Product/ExperienceCheckIn/useCheckInValidationField";
import { getDuration } from "@/helpers/durationHelper";

export const useFirstCheckInCutoffFieldValues = (values: CheckInPreferenceFormValues | null) => {
    const { setValidations } = useCheckInValidationField("checkInPreference.");
    const validations = values?.validations;

    const cutoffValidation = getValidationByType(CheckInValidationType.FIRST_CHECK_IN_CUTOFF, validations);

    /** We can directly compute `duration` using `cutoffValidation.duration`, as everytime when user enters a value we update the count validation in validations array. But this may cause a laggy typing experience. So, we have created a separate state for "duration" */
    const [duration, setDuration] = useState<number>(getDurationMinutes(cutoffValidation));
    const availability = cutoffValidation?.availability ?? Availability.AVAILABLE;
    const isEnabled = !!cutoffValidation;

    const handleEnabledToggle = (value: boolean) => {
        const previousValidations = validations ?? [];

        if (value) {
            /** Multi check-in is enabled; we need to add a new `FIRST_CHECK_IN_CUTOFF` validation */
            setValidations(
                getMutatedValidations(CheckInValidationType.FIRST_CHECK_IN_CUTOFF, previousValidations, "upsert", {
                    duration: "P0D",
                    availability: Availability.AVAILABLE,
                }),
            );
            setDuration(0);
            return;
        }

        setValidations(
            getMutatedValidations(CheckInValidationType.FIRST_CHECK_IN_CUTOFF, previousValidations, "remove", {}),
        );
    };

    const handleDurationChange = (value: number) => {
        setDuration(value);

        setValidations(
            getMutatedValidations(CheckInValidationType.FIRST_CHECK_IN_CUTOFF, validations!, "upsert", {
                duration: getDuration(value).toISOString(),
            }),
        );
    };

    useEffect(() => {
        if (cutoffValidation) {
            setDuration(getDurationMinutes(cutoffValidation));
        }
    }, [cutoffValidation]);

    return {
        duration,
        isEnabled,
        availability,
        handleEnabledToggle,
        handleDurationChange,
    };
};
